import { FillBar } from '@alison/ui';
import Box from '@mui/material/Box';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ComponentType, ReactNode } from 'react';
import { formatCompactCurrency, formatPercent } from '../../lib/format';
import { InfoTip } from '../media/shared/InfoTip';
import { DashboardCard } from './DashboardCard';
import type { HeroMetricsData } from './HeroMetrics';
import { PulseIcon } from './icons/PulseIcon';
import { SirenIcon } from './icons/SirenIcon';
import { TrashIcon } from './icons/TrashIcon';
import { TALL_CARD_HEIGHT } from './layout';
import { useTrackTooltip } from './TooltipTracking';
import { TOOLTIPS } from './tooltips';

interface MoneyRowProps {
  Icon: ComponentType<SvgIconProps>;
  label: string;
  tip: string;
  spend: number;
  share: number;
  currencySymbol: string;
  color: string;
  emphasized?: boolean;
  noData?: boolean;
}

function MoneyRow({
  Icon,
  label,
  tip,
  spend,
  share,
  currencySymbol,
  color,
  emphasized = false,
  noData = false,
}: MoneyRowProps): ReactNode {
  const trackTooltip = useTrackTooltip();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Icon sx={{ fontSize: 20, color: 'text.muted' }} />
        <Typography
          sx={{
            flex: 1,
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: 10,
            color: 'text.secondary',
          }}
        >
          {noData ? label : `${label} (${formatPercent(share, 0)})`}
        </Typography>
        <InfoTip text={tip} onOpen={() => trackTooltip(label)} />
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: emphasized ? 700 : 600,
            color: emphasized ? 'error.dark' : 'text.primary',
          }}
        >
          {noData ? '—' : formatCompactCurrency(spend, currencySymbol)}
        </Typography>
      </Box>
      <FillBar color={color} percent={noData ? 0 : share * 100} label={label} />
    </Box>
  );
}

export function WhereIsTheMoneyCard({
  data,
  currencySymbol,
  noData = false,
}: {
  data: HeroMetricsData['whereIsTheMoney'];
  currencySymbol: string;
  // No spend at all in scope: show dashes, not a row of zeroes that reads as a result.
  noData?: boolean;
}): ReactNode {
  const theme = useTheme();
  return (
    <DashboardCard
      title="Where Is the Money"
      tip={TOOLTIPS.whereIsTheMoney}
      height={TALL_CARD_HEIGHT}
    >
      <MoneyRow
        Icon={PulseIcon}
        label="Working"
        tip={TOOLTIPS.working}
        spend={data.working.spend}
        share={data.working.share}
        currencySymbol={currencySymbol}
        noData={noData}
        color={theme.palette.success.dark}
      />
      <MoneyRow
        Icon={SirenIcon}
        label="At risk"
        tip={TOOLTIPS.atRisk}
        spend={data.atRisk.spend}
        share={data.atRisk.share}
        currencySymbol={currencySymbol}
        noData={noData}
        color={theme.palette.score.medium.text}
      />
      <MoneyRow
        Icon={TrashIcon}
        label="Wasted"
        tip={TOOLTIPS.wasted}
        spend={data.wasted.spend}
        share={data.wasted.share}
        currencySymbol={currencySymbol}
        noData={noData}
        color={theme.palette.error.dark}
        emphasized
      />
    </DashboardCard>
  );
}
